import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ArticleDetailDto,
  CreateArticleInput,
  UpdateArticleInput,
} from '@zoxo/shared';

import { api } from '../../lib/api';
import { articleDetailKey } from './use-article';

/**
 * Article write mutations (POST/PATCH/DELETE /articles, V4 §C). Plain mutations
 * (not Suspense) per the frontend rule. Create returns the detail (isActive=false
 * — pending approval). On success the list (['articles']) is invalidated.
 */

export function useCreateArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateArticleInput) =>
      api.post<ArticleDetailDto>('/articles', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });
}

export function useUpdateArticle(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateArticleInput) =>
      api.patch<ArticleDetailDto>(`/articles/${id}`, input),
    onSuccess: (detail) => {
      queryClient.setQueryData(articleDetailKey(id, true), detail);
      void queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });
}

/** POST /articles/:id/submit — move a 초안(draft) into the review queue. */
export function useSubmitArticle(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ArticleDetailDto>(`/articles/${id}/submit`, {}),
    onSuccess: (detail) => {
      queryClient.setQueryData(articleDetailKey(id, true), detail);
      void queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });
}

export function useDeleteArticle(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<void>(`/articles/${id}`),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['article', id] });
      void queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });
}
